import type { CSSProperties, ReactNode } from "react";
import { tokens } from "@/art/tokens";
import type { ItemRarity, RarityDisplay } from "@/api/types";
import { paletteFor, RARITY_PALETTE } from "@/utils/stats";
import { cn } from "@/utils/cn";

interface RarityFrameProps {
    rarity?: RarityDisplay | ItemRarity | null;
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
    showLabel?: boolean;
    label?: string;
}

/**
 * 아이템 카드·카드 홀더 등에 두르는 레어도 색 테두리 프레임.
 * rarityDisplay.paletteToken 이 슬롯 색과 글로우 결정.
 */
export function RarityFrame({
    rarity,
    children,
    className,
    style,
    showLabel = false,
    label,
}: RarityFrameProps) {
    const palette = paletteFor(rarity);
    const labelKo = label ?? (typeof rarity === "object" && rarity !== null && "labelKo" in rarity ? rarity.labelKo : undefined);

    return (
        <div
            className={cn("relative rounded-m", className)}
            style={{
                border: `1px solid ${palette.border}`,
                background: palette.fillSoft,
                boxShadow: `0 0 24px ${palette.glow}, inset 0 0 36px ${tokens.color.ink}88, inset 0 1px 0 ${palette.border}44`,
                ...style,
            }}
        >
            {/* 2026-04-23: 좌측 accent — Spirytale 언어 통일. */}
            <div
                aria-hidden
                className="absolute inset-y-[6px] left-0 rounded-r-pill"
                style={{
                    width: 2,
                    background: `linear-gradient(180deg, ${palette.border} 0%, ${palette.border}44 100%)`,
                    boxShadow: `0 0 8px ${palette.glow}`,
                    opacity: 0.85,
                }}
            />
            {showLabel && labelKo && (
                <div
                    className="absolute -top-[10px] left-m px-s flex items-center gap-[6px] font-body"
                    style={{
                        background: tokens.color.ink,
                        color: palette.text,
                        fontSize: 12.5,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        opacity: 0.95,
                        border: `1px solid ${palette.border}44`,
                        borderRadius: 999,
                        paddingTop: 1,
                        paddingBottom: 1,
                        boxShadow: `0 0 10px ${palette.glow}`,
                    }}
                >
                    <span
                        aria-hidden
                        style={{
                            width: 8,
                            height: 1,
                            background: palette.text,
                            boxShadow: `0 0 4px ${palette.text}`,
                            opacity: 0.8,
                        }}
                    />
                    {labelKo}
                </div>
            )}
            {children}
        </div>
    );
}

/**
 * 편의 — paletteToken 문자열만 있을 때.
 */
export function paletteForToken(token?: string) {
    if (!token) return RARITY_PALETTE.slate;
    return RARITY_PALETTE[token] ?? RARITY_PALETTE.slate;
}
